import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/admin/ImageUpload";
import RichTextEditor from "@/components/admin/RichTextEditor";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: "", meta_title: "", meta_description: "", published: false };

export default function BlogEditor() {
    const { id } = useParams();
    const isNew = !id || id === "new";
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        api.get(`/admin/blog/${id}`)
            .then(({ data }) => { setForm({ ...data, tags: (data.tags || []).join(", "), cover_image: data.cover_image || "", meta_title: data.meta_title || "", meta_description: data.meta_description || "" }); })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").replace(/-+/g, "-");

    const update = (k, v) => {
        setForm((prev) => {
            const next = { ...prev, [k]: v };
            if (k === "title" && (!prev.slug || prev.slug === slugify(prev.title))) next.slug = slugify(v);
            return next;
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
            if (isNew) { const { data } = await api.post("/admin/blog", payload); toast.success("Article créé"); navigate(`/admin/blog/${data.id}`, { replace: true }); }
            else { await api.put(`/admin/blog/${id}`, payload); toast.success("Article mis à jour"); }
        } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };

    if (loading) return <div className="p-10 text-brand-sageDark">Chargement…</div>;

    return (
        <div className="p-10 max-w-4xl" data-testid="admin-blog-editor">
            <button onClick={() => navigate("/admin/blog")} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-brand-sage hover:text-brand-sageDark mb-6"><ArrowLeft size={14} /> Retour à la liste</button>
            <span className="overline">{isNew ? "Nouveau" : "Édition"}</span>
            <h1 className="mt-3 font-serif text-4xl text-brand-ink font-light">{isNew ? "Nouvel article" : form.title}</h1>
            <form onSubmit={submit} className="mt-10 space-y-8">
                <div><label className="overline block mb-2">Titre *</label><input type="text" value={form.title} onChange={(e) => update("title", e.target.value)} className="field-input" required data-testid="editor-title" /></div>
                <div className="grid md:grid-cols-2 gap-8">
                    <div><label className="overline block mb-2">Slug (URL)</label><input type="text" value={form.slug} onChange={(e) => update("slug", e.target.value)} className="field-input" placeholder="Auto-généré depuis le titre" /></div>
                    <div><label className="overline block mb-2">Tags (séparés par virgule)</label><input type="text" value={form.tags} onChange={(e) => update("tags", e.target.value)} className="field-input" placeholder="Cuisine, Tendance, 2026" /></div>
                </div>
                <ImageUpload label="Image de couverture" value={form.cover_image} onChange={(url) => update("cover_image", url)} testId="editor-cover-upload" />
                <div><label className="overline block mb-2">Extrait *</label><textarea rows={3} value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} className="field-input field-textarea" required /></div>
                <div>
                    <label className="overline block mb-2">Contenu *</label>
                    <RichTextEditor value={form.content} onChange={(html) => update("content", html)} placeholder="Rédigez votre article…" testId="editor-content-rich" />
                </div>
                <div className="grid md:grid-cols-2 gap-8 pt-4 border-t border-brand-border">
                    <div><label className="overline block mb-2">Meta title (SEO)</label><input type="text" value={form.meta_title} onChange={(e) => update("meta_title", e.target.value)} className="field-input" /></div>
                    <div><label className="overline block mb-2">Meta description (SEO)</label><input type="text" value={form.meta_description} onChange={(e) => update("meta_description", e.target.value)} className="field-input" /></div>
                </div>
                <label className="flex items-center gap-3 cursor-pointer pt-4"><input type="checkbox" checked={form.published} onChange={(e) => update("published", e.target.checked)} className="w-4 h-4 accent-brand-sage" /><span className="text-sm text-brand-ink">Publier cet article (visible sur le site)</span></label>
                <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-8 py-4 bg-brand-ink text-brand-bg text-xs uppercase tracking-[0.22em] hover:bg-brand-sage transition-colors disabled:opacity-50"><Save size={14} />{saving ? "Enregistrement…" : "Enregistrer"}</button>
            </form>
        </div>
    );
}
